/**
 * Campaign hotel image SSOT for order cards/details.
 * Persisted URL → campaign metadata hero → caller may then use Gimmonix /getImage.
 *
 * Campaign heroes under `/campaigns/` are SPA public assets and must not be
 * rewritten onto the API origin.
 */
import { getDestinationCampaignHotelByCode } from "../../data/destinationCampaignHotels.js";
import { absolutizeHotelImageUrlForDisplay } from "./orderHotelImageService.js";
import {
  coerceCampaignClientsInfo,
  resolveCampaignHotelContext,
  resolveCampaignSelection,
  resolveCampaignSlugFromOrder,
} from "./campaignOrderContext.js";

function trimUrl(raw) {
  if (raw == null) return "";
  const t = String(raw).trim();
  return t;
}

function asCampaignDisplayUrl(raw) {
  const t = trimUrl(raw);
  if (!t) return null;
  if (t.startsWith("/campaigns/")) return t;
  return absolutizeHotelImageUrlForDisplay(t) || t;
}

function firstCampaignDisplayUrl(...candidates) {
  for (const value of candidates) {
    const url = asCampaignDisplayUrl(value);
    if (url) return url;
  }
  return null;
}

function campaignMetadataImage(slug) {
  if (!slug) return null;
  const row = getDestinationCampaignHotelByCode(slug);
  if (!row || typeof row !== "object") return null;
  return firstCampaignDisplayUrl(row.image, row.heroImage, row.hero_image, row.hotel_image_url);
}

/**
 * @param {Record<string, unknown>|null|undefined} order
 * @returns {string|null}
 */
export function resolveCampaignHotelImageUrl(order) {
  if (!order || typeof order !== "object") return null;

  const ctx = resolveCampaignHotelContext(order) || {};
  const ci = coerceCampaignClientsInfo(order);
  const selection = resolveCampaignSelection(order);

  const persisted = firstCampaignDisplayUrl(
    ctx.hotel_image_url,
    ctx.hotelImageUrl,
    ctx.image,
    ci.hotel_image_url,
    ci.hotelImageUrl,
    selection.hotel_image_url,
  );
  if (persisted) return persisted;

  const slug = resolveCampaignSlugFromOrder(order);
  return campaignMetadataImage(slug);
}

/**
 * Resolve a campaign hero from slug/code alone (checkout / cards without a full order).
 * @param {unknown} campaignOrCode
 * @returns {string|null}
 */
export function resolveCampaignMetadataHotelImage(campaignOrCode) {
  const code =
    typeof campaignOrCode === "string"
      ? campaignOrCode
      : String(campaignOrCode?.hotelCode || campaignOrCode?.slug || campaignOrCode?.campaign || "").trim();
  return campaignMetadataImage(code);
}
